import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const IMGS = {
  hero: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1800&q=85&fit=crop',
  feature: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=1400&q=80&fit=crop',
  library: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1400&q=80&fit=crop',
  graduation: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1400&q=80&fit=crop',
};

const Home: React.FC = () => (
  <>
    <Navbar />

    {/* Hero */}
    <section
      className="hero"
      style={{
        backgroundImage: `linear-gradient(160deg, rgba(4,15,30,0.88) 0%, rgba(11,37,69,0.80) 60%, rgba(13,46,88,0.82) 100%), url(${IMGS.hero})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="hero-content">
        <span className="hero-eyebrow">Ranked No. 33 Among Global Online Universities</span>
        <h1>Innovate. Educate. Elevate.</h1>
        <p>
          The University of American Atlantic delivers accredited, world-class online education
          to students from more than 60 countries — combining academic rigor with the flexibility
          of advanced digital learning technology.
        </p>
        <div className="hero-actions">
          <Link to="/apply" className="btn-primary">Apply for Admission</Link>
          <Link to="/programs" className="btn-secondary">Explore Academic Programs</Link>
        </div>
      </div>
    </section>

    {/* Stats */}
    <div className="stats-bar">
      <div className="stats-bar-inner">
        <div className="stat-item"><h3>60+</h3><p>Countries Represented</p></div>
        <div className="stat-item"><h3>No. 33</h3><p>Global Online Ranking</p></div>
        <div className="stat-item"><h3>40+</h3><p>Academic Programs</p></div>
        <div className="stat-item"><h3>100%</h3><p>Online &amp; Fully Flexible</p></div>
      </div>
    </div>

    {/* Why UAA */}
    <section className="section">
      <div className="container">
        <span className="section-eyebrow">Why UAA</span>
        <h2 className="section-title">A New Standard in Online Higher Education</h2>
        <div className="divider"></div>
        <p className="section-lead">
          UAA integrates artificial intelligence and advanced digital tools to deliver
          a personalized, rigorous academic experience — available to every student,
          anywhere in the world.
        </p>
        <div className="feature-grid">
          {[
            { num: '01', title: 'AI-Powered Learning Environment', desc: 'Adaptive AI-supported modules and virtual instructors personalize the pace and content of instruction for each student.' },
            { num: '02', title: 'Global Accessibility', desc: 'Our platform operates 24 hours a day, seven days a week, serving students across more than 60 countries without geographic limitation.' },
            { num: '03', title: 'Recognition of Prior Learning', desc: 'Professional experience, certifications, and industry training may be converted into academic credit through our RPL framework.' },
            { num: '04', title: 'Verified Academic Credentials', desc: 'All UAA diplomas are issued with QR-secured digital verification through the USDIS platform, ensuring global authenticity.' },
            { num: '05', title: 'Accelerated Degree Pathways', desc: 'Students may complete degrees faster through CLEP examinations, competency-based education, and credit transfer recognition.' },
            { num: '06', title: 'Comprehensive Student Support', desc: "Academic advisors, career counselors, and technical support staff are available throughout each student's academic journey." },
          ].map(f => (
            <div className="feature-card" key={f.num}>
              <div className="fc-num">{f.num}</div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Campus Life Banner */}
    <div
      style={{
        height: 340,
        backgroundImage: `linear-gradient(90deg, rgba(11,37,69,0.92) 0%, rgba(11,37,69,0.6) 55%, rgba(11,37,69,0.2) 100%), url(${IMGS.feature})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        padding: '0 24px',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        <span style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'rgba(184,150,46,0.85)', display: 'block', marginBottom: 14 }}>AI-Powered Education</span>
        <h2 style={{ fontFamily: "'Georgia', serif", color: '#fff', fontSize: 'clamp(1.5rem, 3vw, 2rem)', maxWidth: 540, lineHeight: 1.3, marginBottom: 20 }}>
          Personalized learning paths driven by advanced digital technology.
        </h2>
        <Link to="/about" className="btn-primary">Learn About Our Model</Link>
      </div>
    </div>

    {/* Programs */}
    <section className="section section-navy">
      <div className="container">
        <span className="section-eyebrow">Academic Programs</span>
        <h2 className="section-title">Four Faculties. Undergraduate, Graduate &amp; Doctoral Degrees.</h2>
        <div className="divider"></div>
        <p className="section-lead">
          UAA offers over 40 programs across Engineering, Business, Social Sciences,
          and Health Sciences — all available at Bachelor's, Master's, and Doctoral levels.
        </p>
        <div className="programs-grid">
          {[
            { dept: 'Engineering & Technology', items: ['Software Engineering', 'Artificial Intelligence & Data Science', 'Computer Science', 'Electrical & Electronic Engineering', 'Mechanical Engineering'] },
            { dept: 'Economics & Business Administration', items: ['Business Management', 'Global Trade & Logistics', 'Accounting & Finance', 'Marketing', 'Entrepreneurship & Innovation'] },
            { dept: 'Social Sciences & Humanities', items: ['Psychology', 'Political Science & International Relations', 'Sociology', 'Legal Studies', 'Educational Sciences'] },
            { dept: 'Health & Life Sciences', items: ['Healthcare Administration', 'Nutrition & Dietetics', 'Biomedical Engineering', 'Biotechnology'] },
          ].map(g => (
            <div className="program-card" key={g.dept}>
              <div className="dept">{g.dept}</div>
              <ul>
                {g.items.map(i => <li key={i}>{i}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 40 }}>
          <Link to="/programs" className="btn-primary">View All Academic Programs</Link>
        </div>
      </div>
    </section>

    {/* Library Banner */}
    <div
      style={{
        height: 320,
        backgroundImage: `linear-gradient(90deg, rgba(4,15,30,0.15) 0%, rgba(4,15,30,0.75) 60%, rgba(4,15,30,0.95) 100%), url(${IMGS.library})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 24px',
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ maxWidth: 480, textAlign: 'right' }}>
          <span style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'rgba(184,150,46,0.85)', display: 'block', marginBottom: 14 }}>Academic Resources</span>
          <h2 style={{ fontFamily: "'Georgia', serif", color: '#fff', fontSize: 'clamp(1.4rem, 2.5vw, 1.9rem)', lineHeight: 1.3, marginBottom: 20 }}>
            Access to world-class digital libraries, research databases, and academic resources.
          </h2>
          <Link to="/programs" className="btn-primary">Explore Programs</Link>
        </div>
      </div>
    </div>

    {/* RPL */}
    <section className="section section-light">
      <div className="container">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72, alignItems: 'center' }}>
          <div>
            <span className="section-eyebrow">Recognition of Prior Learning</span>
            <h2 className="section-title">Your Experience Has Academic Value</h2>
            <div className="divider"></div>
            <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.93rem', color: 'var(--text-light)', lineHeight: 1.8, marginBottom: 20 }}>
              The UAA Recognition of Prior Learning (RPL) system formally acknowledges that learning occurs not only in the classroom, but also through professional practice, research, and real-world experience.
            </p>
            <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.93rem', color: 'var(--text-light)', lineHeight: 1.8, marginBottom: 28 }}>
              Through RPL, students may begin their studies at an advanced level, receive exemptions from redundant coursework, shorten their total study duration, and reduce tuition costs — while receiving full academic recognition for their expertise.
            </p>
            <Link to="/apply" className="btn-primary" style={{ display: 'inline-block' }}>Apply Through RPL Track</Link>
          </div>
          <div>
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderTop: '3px solid var(--gold)', borderRadius: 3, padding: 32 }}>
              <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 16 }}>Eligible Experience Types</div>
              <ul style={{ listStyle: 'none' }}>
                {['Professional work experience', 'Industry certifications', 'Management and leadership roles', 'Technical expertise and publications', 'Research, projects, and fieldwork'].map(i => (
                  <li key={i} style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.88rem', color: 'var(--text-light)', padding: '8px 0', borderBottom: '1px solid var(--border)', paddingLeft: 14, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: 'var(--gold)', fontWeight: 700 }}>—</span>
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Graduation Banner */}
    <div
      style={{
        backgroundImage: `linear-gradient(160deg, rgba(4,15,30,0.82) 0%, rgba(11,37,69,0.7) 100%), url(${IMGS.graduation})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        padding: '80px 24px',
        textAlign: 'center',
      }}
    >
      <div className="container">
        <span style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'rgba(184,150,46,0.85)', display: 'block', marginBottom: 14 }}>Begin Your Journey</span>
        <h2 style={{ fontFamily: "'Georgia', serif", color: '#fff', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', marginBottom: 14, lineHeight: 1.3 }}>Ready to Apply to UAA?</h2>
        <div style={{ width: 48, height: 3, background: 'var(--gold)', margin: '0 auto 24px' }}></div>
        <p style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', maxWidth: 520, margin: '0 auto 36px', lineHeight: 1.75 }}>
          Join students from more than 60 countries and begin your academic career at the University of American Atlantic.
        </p>
        <Link to="/apply" className="btn-primary">Start Your Application</Link>
      </div>
    </div>

    {/* Accreditations */}
    <section className="section section-dark">
      <div className="container">
        <span className="section-eyebrow">Recognition &amp; Accreditation</span>
        <h2 className="section-title">International Memberships &amp; Standards</h2>
        <div className="divider"></div>
        <p className="section-lead">
          UAA maintains active collaborations with leading international educational organizations and adheres to globally recognized academic standards.
        </p>
        <div className="accred-grid">
          {['USDLA — United States Distance Learning Association', 'AAA — American Accreditation Association', 'AAHEA — American Association of Higher Education', 'IELA — International E-Learning Association', 'ISTE — International Society for Technology in Education', 'ADEIL — Association for Distance Education and Independent Learning', 'ISO Education Standards', 'International Accreditation Forum'].map(a => (
            <span className="accred-badge" key={a}>{a}</span>
          ))}
        </div>
      </div>
    </section>

    <Footer />
  </>
);

export default Home;
